<template>
    <div v-bind="setId" class="richText" v-if="editor">
        <menu-bar class="richText__header" :editor="editor"></menu-bar>
        <editor-content class="richText__content" :editor="editor" :data-cy="editorContentDataCy" />
    </div>
</template>

<script lang="ts">
import { Editor, EditorContent } from "@tiptap/vue-3";
import { debounce } from "lodash";
import MenuBar from "./MenuBar.vue";

// Starterkit extensions
import Document from "@tiptap/extension-document";
import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import Heading from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import Italic from "@tiptap/extension-italic";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Strike from "@tiptap/extension-strike";
import Text from "@tiptap/extension-text";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";

// Custom or extended Extensions

export default {
    components: {
        EditorContent,
        MenuBar,
    },
    props: {
        id: {
            type: String,
            default: "",
        },
        "model-value": {
            type: String,
            default: "",
        },
        editorContentDataCy: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            editor: null,
            wordsCount: 0,
            scrollY: null,
        };
    },
    computed: {
        setId() {
            if (this.id) {
                return { id: this.id };
            }
            return {};
        },
    },
    methods: {
        getWordsCount() {
            return this.editor.storage.characterCount.words();
        },
    },
    watch: {
        modelValue(value) {
            // HTML
            const isSame = this.editor.getHTML() === value;

            if (isSame) {
                return;
            }

            this.editor.commands.setContent(this.modelValue, false);
        },
    },
    created() {
        window.addEventListener("scroll", () => {
            this.scrollY = window.scrollY;
        });
        window.addEventListener("keydown", this.toggleFullScren);

        this.debounceInput = debounce(() => {
            this.$emit("input", this.editor.getHTML());
        }, 500);
        this.debounceGetWordsCount = debounce(() => {
            this.wordsCount = this.getWordsCount();
        }, 500);
    },
    beforeMount() {
        this.editor = new Editor({
            extensions: [
                Bold,
                BulletList,
                Document,
                Heading,
                Highlight,
                Italic,
                ListItem,
                OrderedList,
                Paragraph,
                Strike,
                Text,
                TextAlign.configure({
                    types: ["heading", "paragraph", "image"],
                }),
                Underline,
                Placeholder.configure({
                    placeholder: "Message...",
                }),
                Image.configure({
                    inline: true, // Permet d'insérer des images dans le texte
                    allowBase64: true, // Permet d'utiliser des images en base64
                }),
                Link.configure({
                    openOnClick: true, // Ouvre le lien lors d'un clic
                    autolink: true, // Transforme automatiquement les URLs en liens
                    linkOnPaste: true, // Transforme les URLs collées en liens
                }),
            ],
            content: this.modelValue,
            onUpdate: () => {
                // pushing scrollbar to the bottom, if we are scrolled to the bottom
                const proseMirror = this.$el.getElementsByClassName("ProseMirror");
                if (proseMirror.length) {
                    const isScrolledToBottom =
                        proseMirror[0].scrollHeight - proseMirror[0].clientHeight <
                        Math.floor(proseMirror[0].scrollTop) + 41;
                    if (isScrolledToBottom) {
                        proseMirror[0].scrollTop = proseMirror[0].scrollHeight;
                    }
                }
                this.$emit("update:model-value", this.editor.getHTML());
                // this.debounceInput();
                // this.debounceGetWordsCount();
            },
        });
    },
    // beforeDestroy() {
    //     this.editor.destroy();
    // },
    // destroyed() {
    //     window.removeEventListener("keydown", this.toggleFullScren);
    // },
};
</script>

<style lang="scss">
.richText {
    display: flex;
    flex-direction: column;
    max-height: 25rem;
    color: #0d0d0d;
    background-color: #ececec;
    //border: 1px solid #b3b3b3;
    border-radius: 12px;
    padding: px-2 py-0;
}
.richText__header {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    flex-wrap: wrap;
    padding: 0.25rem;
    border-bottom: 1px solid #b3b3b3;
}

.richText__content {
    padding: 1.25rem 1rem;
    flex: 1 1 auto;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    cursor: auto;
}

.richText__footer {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    border-top: 1px solid #b3b3b3;
    font-size: 12px;
    font-weight: 600;
    color: #0d0d0d;
    white-space: nowrap;
    padding: 0.25rem 0.75rem;
}

.richText__menu-item {
    width: 1.75rem;
    height: 1.75rem;
    color: #0d0d0d;
    border: none;
    background-color: transparent;
    border-radius: 0.4rem;
    padding: 0.25rem;
    margin-right: 0.25rem;
    font-weight: bold;
    cursor: pointer;
    font-family: Georgia, serif;
}

.remix {
    width: 1.2rem;
    height: 1.2rem;
    fill: #333;
}
.remix.active {
    filter: invert(99%) sepia(93%) saturate(2%) hue-rotate(156deg) brightness(110%) contrast(100%);
}

.richText__menu-item.active,
.richText__menu-item:hover {
    color: #fff;
    background-color: #5fc9b8;
}
.richText__content :focus-visible {
    outline: none;
}
.tiptap {
    /* List styles */
    ul,
    ol {
        padding: 0 1rem;

        li p {
            margin-top: 0.25em;
            margin-bottom: 0.25em;
        }
    }
    img {
        max-width: 100%;
    }
}
</style>
